// Copyright (c) Move Closer 2022.

import { Component, Mixins } from 'vue-property-decorator'
import {
  AnyObject,
  EventbusType,
  EventPayload,
  IEventbus,
  IModal,
  ModalType
} from '@movecloser/front-core'
import throttle from 'lodash/throttle'

import { DrawerLayout } from '../../../../dsl/molecules/Drawer/Drawer.config'
import { DrawerType, IDrawer } from '../../../shared/contracts/services'
import { Inject, logger } from '../../../../support'
import { CartAnalyticsMixin } from '../../../shared/mixins/cart-analytics.mixin'
import { StructureConfigurable } from '../../../../support/mixins'
import { UseListeners } from '../../../../modules/mixins/UseListeners'

import { Drawers, Modals } from '../../config'
import { ToastMixin } from '../../../shared'
import { toImageProps } from '../../../shared/support'

import { CartMutationTypes } from '../../contracts'

import { BaseCartMixin, IBaseCart } from './base-cart.mixin'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
@Component
export class ProductCartMixin extends Mixins<IBaseCart, ToastMixin, CartAnalyticsMixin, StructureConfigurable, UseListeners>(
  BaseCartMixin,
  ToastMixin,
  CartAnalyticsMixin,
  StructureConfigurable,
  UseListeners
) {
  @Inject(DrawerType, false)
  protected readonly drawerConnector?: IDrawer

  @Inject(EventbusType)
  protected readonly eventBus!: IEventbus

  @Inject(ModalType)
  protected readonly modalConnector!: IModal

  public mobileNavHeight: number | null = null
  public drawerHeight: number | null = null

  /**
   * Handles adding product to cart.
   *
   * FIXME: Here should be generic ProductVariant contract from context.
   */
  public async addToCart (
    product: AnyObject,
    quantity: number = 1,
    openModal: boolean = true,
    modalSize: string = 'medium',
    useDrawer: boolean = false,
    shouldHaveAction: boolean = true
  ): Promise<void> {
    try {
      if (this.cartId === null) {
        this.isLoggedInUser ? await this.createCustomerCart() : await this.createCart()
      }

      const cart = await this.cartService.addToCart(this.cartId, product.sku, quantity)
      const filtered = cart.items.filter(item => {
        const variant = this.getFirstVariant(item)
        return variant.sku === product.sku
      })
      const addedProduct = (filtered.length > 0) ? filtered[0] : null
      if (addedProduct) {
        this.eventBus.emit('app:cart.add', this.getCartAddPayload(addedProduct, quantity, cart))
        this.eventBus.emit('app:cart.view', this.getCartViewPayload(cart))
      }
      this.$store.commit(CartMutationTypes.SetCartId, cart.id)
      this.$store.commit(CartMutationTypes.SetCart, cart)

      const payload = {
        product: {
          brand: product.attributes.brand ?? '',
          category: product.attributes.mainCategory ?? '',
          description: product.description,
          image: (Array.isArray(product.images) && product.images.length > 0) ? toImageProps(product.images[0]) : {
            src: '',
            alt: ''
          },
          isFaF: product.attributes.isFaF ?? false,
          price: product.price.finalPrice,
          previousPrice: product.price.previousPrice,
          sku: product.sku,
          title: product.name,
          volume: product.attributes.volumeName ?? ''
        },
        shouldHaveAction: shouldHaveAction,
        drawerHeight: this.drawerHeight,
        mobileNavHeight: this.mobileNavHeight
      }

      if (useDrawer) {
        if (!this.drawerConnector) {
          logger('[drawerConnector] is of type undefined!')
          return
        }

        this.drawerConnector.open(Drawers.AddedToCard, payload, {
          closeOnClickOutside: true,
          layout: DrawerLayout.Partial
        })
      } else {
        if (openModal) {
          this.modalConnector.open(Modals.AddedToCartModal, payload, { size: modalSize })
        }
      }
    } catch (e) {
      const error = e as Error
      this.showToast(error.message, 'danger')
      logger(e, 'warn')
    }
  }

  public onResize (): void {
    this.drawerHeight = window.visualViewport.height - (this.mobileNavHeight ? this.mobileNavHeight : 0)
  }

  created () {
    this.eventBus.handle('app:mobile-nav-height', (event: EventPayload<number>) => {
      if (event.payload) {
        this.mobileNavHeight = event.payload
        this.drawerHeight = window.visualViewport.height - (this.mobileNavHeight ? this.mobileNavHeight : 0)
      }
    })
  }

  mounted () {
    this.addEventListener('resize', throttle(this.onResize, 100))
  }
}
